import { useState } from 'react';
import type { Override, Carrier, CreateOverrideInput } from '../api/types';
import { SHELVE_VALUES, CONFIRMED_VALUES, GDS_VALUES, SHELVE_LABELS } from '../api/types';
import { createOverride, updateOverride } from '../api/client';
import { ConflictWarning } from './ConflictWarning';

interface Props {
  carriers: Carrier[];
  editingOverride?: Override | null;
  onSaved: () => void;
  onCancel: () => void;
}

const empty: CreateOverrideInput = {
  carrier_code: '',
  fare_family: '',
  correct_shelve: 'TWO_STARS',
  wrong_shelve: '',
  confirmed: 'No',
  gds: '',
  booking_code: '',
  international: '',
  fare_basis: '',
  comments: '',
};

function overrideToInput(o: Override): CreateOverrideInput {
  return {
    carrier_code: o.carrierCode,
    booking_code: o.bookingCode || '',
    fare_family: o.fareFamily,
    gds: o.gds || '',
    correct_shelve: o.correctShelve,
    wrong_shelve: o.wrongShelve || '',
    confirmed: o.confirmed,
    international: o.international || '',
    fare_basis: o.fareBasis || '',
    comments: o.comments || '',
  };
}

export function OverrideForm({
  carriers,
  editingOverride,
  onSaved,
  onCancel,
}: Props) {
  const [form, setForm] = useState<CreateOverrideInput>(
    editingOverride ? overrideToInput(editingOverride) : empty
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conflictRules, setConflictRules] = useState<any[]>([]);

  const set = (key: keyof CreateOverrideInput, value: string) => {
    setForm((f) => ({ ...f, [key]: value }));
    setError(null);
    setConflictRules([]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setConflictRules([]);

    try {
      // Clean empty strings to null
      const cleaned: CreateOverrideInput = {
        ...form,
        booking_code: form.booking_code || null,
        gds: form.gds || null,
        wrong_shelve: form.wrong_shelve || null,
        international: form.international || null,
        fare_basis: form.fare_basis || null,
        comments: form.comments || null,
      };

      if (editingOverride) {
        await updateOverride(editingOverride.id, cleaned);
      } else {
        await createOverride(cleaned);
      }
      onSaved();
    } catch (err: any) {
      if (err.status === 409 && err.details?.conflicting_rules) {
        setConflictRules(err.details.conflicting_rules);
        setError(err.message);
      } else {
        setError(err.message || 'An error occurred');
      }
    } finally {
      setSaving(false);
    }
  };

  const isEditing = !!editingOverride;

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto"
      >
        <h2 className="text-lg font-semibold mb-4">
          {isEditing ? `Edit Override #${editingOverride.id}` : 'New Override'}
        </h2>

        {error && (
          <div className="mb-3 text-sm text-red-600 bg-red-50 p-2 rounded">
            {error}
          </div>
        )}
        {conflictRules.length > 0 && (
          <div className="mb-3">
            <ConflictWarning conflictingRules={conflictRules} />
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-0.5">
              Carrier Code *
            </label>
            <select
              value={form.carrier_code}
              onChange={(e) => set('carrier_code', e.target.value)}
              required
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
            >
              <option value="">Select...</option>
              {carriers.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.code} - {c.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-0.5">
              Fare Family *
            </label>
            <input
              type="text"
              value={form.fare_family}
              onChange={(e) => set('fare_family', e.target.value)}
              required
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-0.5">
              Correct Shelf *
            </label>
            <select
              value={form.correct_shelve}
              onChange={(e) => set('correct_shelve', e.target.value)}
              required
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
            >
              {SHELVE_VALUES.map((s) => (
                <option key={s} value={s}>
                  {s} ({SHELVE_LABELS[s]})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-0.5">
              Wrong Shelf
            </label>
            <select
              value={form.wrong_shelve || ''}
              onChange={(e) => set('wrong_shelve', e.target.value)}
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
            >
              <option value="">None</option>
              {SHELVE_VALUES.map((s) => (
                <option key={s} value={s}>
                  {s} ({SHELVE_LABELS[s]})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-0.5">
              Confirmed *
            </label>
            <select
              value={form.confirmed}
              onChange={(e) => set('confirmed', e.target.value)}
              required
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
            >
              {CONFIRMED_VALUES.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-0.5">
              GDS
            </label>
            <select
              value={form.gds || ''}
              onChange={(e) => set('gds', e.target.value)}
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
            >
              <option value="">Any</option>
              {GDS_VALUES.map((v) => (
                <option key={v} value={v}>
                  {v === 's' ? 'Sabre (s)' : 'Amadeus (a)'}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-0.5">
              Booking Code
            </label>
            <input
              type="text"
              value={form.booking_code || ''}
              onChange={(e) => set('booking_code', e.target.value)}
              placeholder="e.g. D,I,J,C,R"
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-0.5">
              International
            </label>
            <input
              type="text"
              value={form.international || ''}
              onChange={(e) => set('international', e.target.value)}
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
            />
          </div>
          <div className="col-span-2">
            <label className="block text-xs font-medium text-gray-600 mb-0.5">
              Comments
            </label>
            <textarea
              value={form.comments || ''}
              onChange={(e) => set('comments', e.target.value)}
              rows={2}
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-1.5 text-sm bg-gray-200 hover:bg-gray-300 rounded"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded disabled:opacity-50"
          >
            {saving ? 'Saving...' : isEditing ? 'Update' : 'Create'}
          </button>
        </div>
      </form>
    </div>
  );
}
